import type {LinkXyiconRequest, XyiconLinkDetail} from "../../../../../../generated/api/base";
import {ArrowStyle, LineStyle, LinkDirection, XyiconFeature} from "../../../../../../generated/api/base";
import type {Field, IFieldAdapter} from "../../../../../../data/models/field/Field";
import type {IModel} from "../../../../../../data/models/Model";
import type {Xyicon} from "../../../../../../data/models/Xyicon";
import type {AppActions} from "../../../../../../data/state/AppActions";
import type {AppState} from "../../../../../../data/state/AppState";
import type {TransportLayer} from "../../../../../../data/TransportLayer";
import {StringUtils} from "../../../../../../utils/data/string/StringUtils";
import {XHRLoader} from "../../../../../../utils/loader/XHRLoader";
import type {ILookupFieldOption} from "./DetailsContent";

export class LookupUtils {
	public static readonly getLookupFieldOptions = (appState: AppState, field: any, item: IModel) => {
		const items = appState.actions.getList<Xyicon>(XyiconFeature.Xyicon).filter((xyicon) => xyicon !== (item as any as Xyicon));
		return LookupUtils.getOptionsForItems(appState, field, items);
	};

	private static readonly getOptionsForItems = (appState: AppState, field: IFieldAdapter, items: Xyicon[]) => {
		const filteredItems = items
			.map((xyicon: Xyicon) => {
				return {
					value: appState.actions.getFieldValue(xyicon, field?.refId) as string,
					xyicon,
				};
			})
			.filter((o) => o.value) as ILookupFieldOption[]; // Don't list xyicons that don't have the value on the field

		return filteredItems.toSorted((a, b) => StringUtils.sortIgnoreCase(a.value, b.value));
	};

	/** Closed lookup inputs only need labels for their linked, currently available xyicons. */
	public static readonly getSelectedLookupFieldOptionsForField = (appState: AppState, field: IFieldAdapter, item: IModel) => {
		const linkedIds = new Set(appState.actions.getLinksXyiconXyicon(item.id).map(({object}) => object.id));
		if (linkedIds.size === 0) {
			return [];
		}
		// Keep collection order for equal labels, and exclude links to xyicons that
		// are only preloaded organization-wide, just as the full options list does.
		const linkedItems = appState.actions.getList<Xyicon>(XyiconFeature.Xyicon).filter((xyicon) => xyicon !== item && linkedIds.has(xyicon.id));
		return LookupUtils.getOptionsForItems(appState, field, linkedItems);
	};

	public static readonly getSelectedLookupFieldOptions = (appState: AppState, lookupFieldOptions: ILookupFieldOption[], item: IModel) => {
		const existingLinks = appState.actions.getLinksXyiconXyicon(item.id);

		return lookupFieldOptions.filter((lookupFieldOption) => {
			for (const link of existingLinks) {
				if (link.object.id === lookupFieldOption.xyicon.id) {
					return true;
				}
			}
			return false;
		});
	};

	public static readonly getOnChangeForLookup = (
		transport: TransportLayer,
		appState: AppState,
		lookupFieldOptions: ILookupFieldOption[],
		item: IModel,
	) => {
		const existingLinks = appState.actions.getLinksXyiconXyicon(item.id);
		const itemPortfolioId = item.portfolioId ?? transport.appState.activePortfolioId;

		return (selectedOptions: ILookupFieldOption[]) => {
			const linkIdsToRemove: string[] = [];
			const linksToAdd: {detail: XyiconLinkDetail; objectBPortfolioId: string}[] = [];

			for (const lookupFieldOption of lookupFieldOptions) {
				const existingLink = existingLinks.find(
					(l) =>
						(l.link.objectAID === item.id && l.link.objectBID === lookupFieldOption.xyicon.id) ||
						(l.link.objectBID === item.id && l.link.objectAID === lookupFieldOption.xyicon.id),
				);

				const isCurrentlySelected = selectedOptions.find((o) => o.xyicon.refId === lookupFieldOption.xyicon.refId);

				if (existingLink) {
					if (!isCurrentlySelected) {
						// Remove existing
						linkIdsToRemove.push(existingLink.link.id);
					}
				} else if (isCurrentlySelected) {
					linksToAdd.push({
						objectBPortfolioId: lookupFieldOption.xyicon.portfolioId ?? itemPortfolioId,
						detail: {
							objectAID: item.id,
							objectBID: lookupFieldOption.xyicon.id,
							objectBPortID: null,
							objectAPortID: null,
							isEmbedded: false,
							direction: LinkDirection.Both,
							settings: {
								lineColor: "#1E88E5",
								lineWidth: 4,
								arrowStyle: ArrowStyle.Filled,
								lineStyle: LineStyle.Solid,
							},
						},
					});
				}
			}

			if (linkIdsToRemove.length > 0) {
				return transport.requestForOrganization({
					url: "xyicons/deletelink",
					method: XHRLoader.METHOD_DELETE,
					params: {
						portfolioID: itemPortfolioId,
						linkIDList: linkIdsToRemove,
					},
				});
			}

			if (linksToAdd.length > 0) {
				const linksByPortfolio = new Map<string, XyiconLinkDetail[]>();
				linksToAdd.forEach(({detail, objectBPortfolioId}) => {
					linksByPortfolio.set(objectBPortfolioId, [...(linksByPortfolio.get(objectBPortfolioId) ?? []), detail]);
				});

				return Promise.all(
					[...linksByPortfolio].map(([objectBPortfolioId, xyiconLinkDetails]) => {
						const params: LinkXyiconRequest = {
							objectAPortfolioID: itemPortfolioId,
							objectBPortfolioID: objectBPortfolioId,
							xyiconLinkDetails,
						};

						return transport.requestForOrganization({
							url: "xyicons/createlink",
							method: XHRLoader.METHOD_PUT,
							params: params,
						});
					}),
				).then((responses) => responses[0]);
			}
		};
	};

	public static readonly isLookupField = (field: IFieldAdapter, firstSelectedItem: IModel, actions: AppActions) => {
		if (field && firstSelectedItem) {
			const type = actions.getTypeById(firstSelectedItem.typeId);
			const xyiconLookupLinkFieldList = type?.settings.xyiconLookupLinkFieldList || [];

			return (field as Field)?.id && xyiconLookupLinkFieldList.includes((field as Field).id);
		}
		return false;
	};
}
